export type Entity<T> = T & { id?: string }

export type CacheEntry<T> = {
	value: T
	expiresAt: number
	extradata?: Record<string, any>
}

export abstract class BaseMemoryCache<T> {
	protected cache = new Map<string, CacheEntry<Entity<T> | T[] | (string | undefined)[]>>()
	private readonly FIFTEEN_MINUTES_MS = 900000

	constructor(protected defaultDurationMs: number = this.FIFTEEN_MINUTES_MS) {}

	get(key: string): Entity<T> | T[] | (string | undefined)[] | undefined {
		const item = this.cache.get(key)
		if (!item) return

		if (Date.now() > item.expiresAt) {
			this.cache.delete(key)
			return
		}

		return item.value
	}

	getMany(keys: string[]): Entity<T>[] {
		return keys.map((key) => this.get(key) as Entity<T>)
	}

	set(value: Entity<T> | T[] | string[], key?: string, durationMs?: number): void {
		const duration = durationMs ?? this.defaultDurationMs
		const expiresAt = Date.now() + duration
		const finalKey = key || this.getId(value)
		if (!finalKey) return console.error('Invalid cache key')

		this.cache.set(finalKey, { value, expiresAt })
	}

	setMany(entities: Entity<T>[], durationMs?: number): void {
		entities.forEach((entity) => this.set(entity, entity.id!, durationMs))
	}

	delete(key: string): void {
		this.cache.delete(key)
	}

	deleteMany(keys: string[]) {
		keys.forEach((key: string) => this.delete(key))
	}

	deleteManyThatStartWith(keyStart: string) {
		const keys: any = this.cache.keys()
		const filteredKeys = keys.filter((key: string) => key.startsWith(keyStart))
		filteredKeys.forEach((key: string) => this.delete(key))
	}

	clear(): void {
		this.cache.clear()
	}

	private getId(value: Entity<T> | T[] | string[]): string | undefined {
		if (!value || Array.isArray(value)) return
		if (typeof value === 'object' && value.id) return value.id
	}

	protected buildParamsKey(params: Record<string, unknown>): string {
		return Object.keys(params)
			.sort()
			.map((key) => `${key}=${this.serializeParam(params[key])}`)
			.join('_')
	}

	private serializeParam(value: unknown): string {
		if (Array.isArray(value)) return value.sort().join('-')
		return String(value)
	}
}
